import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL, formatDate } from "../lib/format";
import { ODEME_YERLERI, bugun } from "../lib/sabitler";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { Banknote, Loader2, Trash2, CheckCircle2, Pencil, X } from "lucide-react";

const BOS_FORM = { tutar: "", tarih: "", alan_kisi: "", odeme_yeri: "", tedarikci: "", aciklama: "" };

export const TahsilatDialog = ({ ciroId, open, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(BOS_FORM);
  const [saving, setSaving] = useState(false);
  const [duzenleId, setDuzenleId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!ciroId) return;
    setLoading(true);
    try {
      const res = await api.get(`/ciro/${ciroId}/tahsilatlar`);
      setData(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Tahsilat bilgileri yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [ciroId]);

  useEffect(() => {
    if (open) {
      setForm({ ...BOS_FORM, tarih: bugun() });
      setDuzenleId(null);
      fetchData();
    }
  }, [open, fetchData]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const duzenleBaslat = (t) => {
    setDuzenleId(t.id);
    setForm({
      tutar: String(t.tutar ?? ""),
      tarih: t.tarih || bugun(),
      alan_kisi: t.alan_kisi || "",
      odeme_yeri: t.odeme_yeri || "",
      tedarikci: t.tedarikci || "",
      aciklama: t.aciklama || "",
    });
  };

  const duzenleIptal = () => {
    setDuzenleId(null);
    setForm({ ...BOS_FORM, tarih: bugun() });
  };

  const kaydet = async () => {
    const tutar = parseFloat(form.tutar);
    if (!tutar || tutar <= 0) return toast.error("Tahsil edilen tutar 0'dan büyük olmalıdır.");
    if (!form.tarih) return toast.error("Tahsilat tarihi zorunludur.");
    if (!form.alan_kisi.trim()) return toast.error("Tahsilatı alan kişi zorunludur.");
    if (!form.odeme_yeri) return toast.error("Nereye ödendiğini seçin.");
    if (form.odeme_yeri === "Tedarikçi Ödemesi" && !form.tedarikci.trim())
      return toast.error("Tedarikçi ödemesi için tedarikçi adı zorunludur.");
    setSaving(true);
    try {
      const govde = {
        tutar,
        tarih: form.tarih,
        alan_kisi: form.alan_kisi.trim(),
        odeme_yeri: form.odeme_yeri,
        tedarikci: form.odeme_yeri === "Tedarikçi Ödemesi" ? form.tedarikci.trim() : null,
        aciklama: form.aciklama.trim(),
      };
      if (duzenleId) {
        await api.put(`/ciro/tahsilat/${duzenleId}`, govde);
        toast.success("Tahsilat güncellendi.");
      } else {
        await api.post(`/ciro/${ciroId}/tahsilat`, govde);
        toast.success("Tahsilat kaydedildi.");
      }
      setDuzenleId(null);
      setForm({ ...BOS_FORM, tarih: bugun() });
      fetchData();
      onChanged?.();
    } catch (err) {
      toast.error(getErrMsg(err, "Tahsilat kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  const sil = async (id) => {
    try {
      await api.delete(`/ciro/tahsilat/${id}`);
      toast.success("Tahsilat kaydı silindi.");
      fetchData();
      onChanged?.();
    } catch (err) {
      toast.error(getErrMsg(err, "Tahsilat silinemedi."));
    }
  };

  const c = data?.ciro;
  const odendi = c && c.kalan <= 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="tahsilat-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Banknote className="h-5 w-5" /> Tahsilat Kaydı
          </DialogTitle>
          <DialogDescription>
            {c ? `${c.belge_no || "Belge no yok"} • ${c.musteri_aciklama || "—"}` : "Yükleniyor…"}
          </DialogDescription>
        </DialogHeader>

        {loading || !c ? (
          <div className="space-y-2">
            <Skeleton className="h-14" />
            <Skeleton className="h-40" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Bakiye özeti */}
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${odendi ? "border-emerald-200 bg-emerald-50/70" : "border-border bg-muted/40"}`}
              data-testid="tahsilat-bakiye-ozeti"
            >
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 tabular-nums">
                <span>Fatura <strong data-testid="tahsilat-fatura-toplam">{formatTL(c.toplam, 2)}</strong></span>
                <span className="text-emerald-700">Tahsil edilen <strong data-testid="tahsilat-tahsil-edilen">{formatTL(c.tahsil_edilen, 2)}</strong></span>
                <span className={c.kalan > 0 ? "text-amber-700" : "text-emerald-700"}>
                  Kalan <strong data-testid="tahsilat-kalan">{formatTL(c.kalan, 2)}</strong>
                </span>
              </div>
              {odendi && (
                <p className="mt-1 flex items-center gap-1 text-xs font-medium text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Fatura tamamen tahsil edildi.
                </p>
              )}
            </div>

            {/* Yeni tahsilat / düzenleme formu */}
            {(!odendi || duzenleId) && (
              <div className={`rounded-lg border p-3.5 space-y-3 ${duzenleId ? "border-sky-300 bg-sky-50/40" : ""}`} data-testid="tahsilat-form">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {duzenleId ? "Tahsilatı Düzenle" : "Yeni Tahsilat"}
                  </p>
                  {duzenleId && (
                    <Button variant="ghost" size="sm" onClick={duzenleIptal} className="h-7 gap-1 text-xs" data-testid="tahsilat-duzenle-iptal-button">
                      <X className="h-3.5 w-3.5" /> Vazgeç
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Tahsil Edilen Tutar * ₺</Label>
                    <Input
                      type="number" inputMode="decimal" step="0.01" min="0" placeholder="0,00"
                      value={form.tutar} onChange={(e) => set("tutar", e.target.value)}
                      className="h-11 text-right tabular-nums" data-testid="tahsilat-tutar-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tarih *</Label>
                    <Input
                      type="date" value={form.tarih} onChange={(e) => set("tarih", e.target.value)}
                      className="h-11" data-testid="tahsilat-tarih-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tahsilatı Alan Kişi *</Label>
                    <Input
                      placeholder="Ad Soyad" value={form.alan_kisi} onChange={(e) => set("alan_kisi", e.target.value)}
                      className="h-11" data-testid="tahsilat-alan-kisi-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nereye Ödendi *</Label>
                    <Select value={form.odeme_yeri} onValueChange={(v) => set("odeme_yeri", v)}>
                      <SelectTrigger className="h-11" data-testid="tahsilat-odeme-yeri-select">
                        <SelectValue placeholder="Seçin" />
                      </SelectTrigger>
                      <SelectContent className="bg-card">
                        {ODEME_YERLERI.map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.odeme_yeri === "Tedarikçi Ödemesi" && (
                    <div className="space-y-1 col-span-2">
                      <Label className="text-xs">Tedarikçi Adı *</Label>
                      <Input
                        placeholder="Tedarikçi adı (listeye otomatik eklenir)"
                        value={form.tedarikci} onChange={(e) => set("tedarikci", e.target.value)}
                        className="h-11" data-testid="tahsilat-tedarikci-input"
                      />
                    </div>
                  )}
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Açıklama</Label>
                    <Input
                      placeholder="Opsiyonel not" value={form.aciklama} onChange={(e) => set("aciklama", e.target.value)}
                      className="h-11" data-testid="tahsilat-aciklama-input"
                    />
                  </div>
                </div>
                <Button onClick={kaydet} disabled={saving} className="min-h-[44px] w-full gap-1.5" data-testid="tahsilat-kaydet-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />}
                  {duzenleId ? "Değişiklikleri Kaydet" : "Tahsilatı Kaydet"}
                </Button>
              </div>
            )}

            {/* Tahsilat geçmişi */}
            <div data-testid="tahsilat-liste">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Tahsilat Geçmişi ({data.tahsilatlar.length})
              </p>
              {data.tahsilatlar.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="tahsilat-liste-bos">Henüz tahsilat kaydı yok.</p>
              ) : (
                <div className="space-y-2">
                  {data.tahsilatlar.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5" data-testid={`tahsilat-satir-${t.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold tabular-nums">{formatTL(t.tutar, 2)}</p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {formatDate(t.tarih)} • {t.alan_kisi} • {t.odeme_yeri}{t.tedarikci ? ` (${t.tedarikci})` : ""}{t.aciklama ? ` • ${t.aciklama}` : ""}
                        </p>
                      </div>
                      <Button
                        variant="ghost" size="icon" onClick={() => duzenleBaslat(t)}
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                        data-testid={`tahsilat-duzenle-${t.id}`} aria-label="Tahsilatı düzenle"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" onClick={() => sil(t.id)}
                        className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
                        data-testid={`tahsilat-sil-${t.id}`} aria-label="Tahsilatı sil"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
